import React, { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import config from '../../config/config';

const ModelViewer = () => {
  const mountRef = useRef(null);
  const { category, sku, sku1, sku2 } = useParams();
  const location = useLocation();

  useEffect(() => {
    // Check if we have valid parameters
    const isComboView = location.pathname.includes('/view/combo/');
    const isSingleView = !isComboView && category && sku;
    
    if (!isComboView && !isSingleView) return;

    // Store ref value in a variable
    const mountNode = mountRef.current;
    if (!mountNode) return;

    // Construct model URLs based on category
    const getModelUrls = () => {
      if (isComboView) {
        // For combo view, first model is necklace (sku1) and second is earrings (sku2)
        return {
          models: [
            `${config.cdnUrl}/models/necklaces/${sku1}/scene.glb`,
            `${config.cdnUrl}/models/earrings/${sku2}/scene.glb`
          ],
          types: ['necklace', 'earring']
        };
      } else {
        const modelUrl = category === 'earrings'
          ? `${config.cdnUrl}/models/${category}/${sku}/scene.glb`
          : category === 'necklaces'
            ? `${config.cdnUrl}/models/${category}/${sku}/scene.glb`
            : null;
        return {
          models: modelUrl ? [modelUrl] : [],
          types: [category === 'earrings' ? 'earring' : 'necklace']
        };
      }
    };

    const { models: modelUrls, types: modelTypes } = getModelUrls();
    if (modelUrls.length === 0) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;
    camera.position.y = 0;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountNode.appendChild(renderer.domElement);

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 100;
    camera.add(directionalLight);
    scene.add(camera);

    // Add hemisphere light for better ambient lighting
    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.5);
    scene.add(hemisphereLight);

    // Add point lights for highlights
    const pointLight1 = new THREE.PointLight(0xffffff, 0.5);
    pointLight1.position.set(5, 5, 5);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0xffffff, 0.3);
    pointLight2.position.set(-5, -5, -5);
    scene.add(pointLight2);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // Model loading
    const loader = new GLTFLoader();
    let loadedModels = 0;
    const totalModels = modelUrls.length;

    const loadModel = (url, index) => {
      loader.load(
        url,
        (gltf) => {
          const model = gltf.scene;
          
          // Position combo models
          if (isComboView) {
            if (modelTypes[index] === 'necklace') {
              model.position.set(-2, 0, 0);
              model.scale.set(1.2, 1.2, 1.2);
            } else {
              model.position.set(2, 0, 0);
              model.scale.set(1, 1, 1);
            }
          } else {
            // Center single model
            model.position.set(0, 0, 0);
            model.scale.set(1, 1, 1);
          }
          
          scene.add(model);
          loadedModels++;
          
          // After all models are loaded, adjust camera
          if (loadedModels === totalModels) {
            // Calculate bounding box of all objects
            const box = new THREE.Box3();
            scene.traverse((object) => {
              if (object.isMesh) {
                box.expandByObject(object);
              }
            });
            
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            
            // Center the scene on the models
            scene.position.sub(center);
            
            // Adjust camera to fit all objects
            const maxDim = Math.max(size.x, size.y, size.z);
            const fov = camera.fov * (Math.PI / 180);
            const cameraZ = Math.abs(maxDim / Math.sin(fov / 2)) * 1.5;
            
            // Position camera to view all models
            camera.position.set(0, 0, cameraZ);
            camera.lookAt(0, 0, 0);
            
            // Update camera and controls
            camera.updateProjectionMatrix();
            controls.update();
          }
        },
        undefined,
        (error) => console.error('Error loading model:', error)
      );
    };

    modelUrls.forEach((url, index) => loadModel(url, index));

    // Animation loop
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);

      // Clear any remaining animation frame callbacks
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      // Dispose of scene resources
      scene.traverse((object) => {
        if (object.isMesh) {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => {
                disposeMaterial(material);
              });
            } else {
              disposeMaterial(object.material);
            }
          }
        }
        if (object.isLight) {
          if (object.shadow && object.shadow.map) {
            object.shadow.map.dispose();
          }
        }
      });

      // Helper function to dispose material and its textures
      function disposeMaterial(material) {
        if (material.map) material.map.dispose();
        if (material.lightMap) material.lightMap.dispose();
        if (material.bumpMap) material.bumpMap.dispose();
        if (material.normalMap) material.normalMap.dispose();
        if (material.specularMap) material.specularMap.dispose();
        if (material.envMap) material.envMap.dispose();
        if (material.alphaMap) material.alphaMap.dispose();
        if (material.aoMap) material.aoMap.dispose();
        if (material.displacementMap) material.displacementMap.dispose();
        if (material.emissiveMap) material.emissiveMap.dispose();
        if (material.gradientMap) material.gradientMap.dispose();
        if (material.metalnessMap) material.metalnessMap.dispose();
        if (material.roughnessMap) material.roughnessMap.dispose();
        material.dispose();
      }

      // Remove and dispose of renderer
      if (mountNode) {
        mountNode.removeChild(renderer.domElement);
      }
      
      // Dispose of render target if it exists
      if (renderer.renderTarget) {
        renderer.renderTarget.dispose();
      }

      // Dispose of all render targets in case of multiple
      renderer.renderTargets?.forEach?.(target => {
        if (target && target.dispose) {
          target.dispose();
        }
      });

      renderer.dispose();
      
      // Clear references to help garbage collection
      controls.dispose?.();
    };
  }, [category, sku, sku1, sku2, location.pathname]);

  return <div ref={mountRef} style={{ width: '100%', height: '100vh' }} />;
};

export default ModelViewer;
